import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Loader2, MapPin, Search, X } from 'lucide-react'

import { coordText, type Place } from '../../lib/geocode'
import type { LatLon } from '../../lib/types'
import { geocodeErrorText, useSearchPlaces } from './geocodeApi'

/**
 * Adresse eintippen, Treffer auswählen, Koordinate übernehmen.
 *
 * Drei Regeln stecken in diesem Bauteil:
 *
 * Gesucht wird auf Knopfdruck, nicht beim Tippen. Der Adressdienst erlaubt eine
 * Anfrage je Sekunde und verbietet Massenabfragen; eine Suche bei jedem
 * Tastendruck wäre genau das. Nebenbei ist sie auch für den Nutzer besser: wer
 * „Kamp Šobec, Lesce“ tippen will, bekommt nicht nach „Kam“ eine Liste, die
 * ihn ablenkt.
 *
 * Übernommen wird nichts von selbst. Ein Treffer setzt die Koordinate erst,
 * wenn jemand ihn angetippt hat - auch dann nicht, wenn es der einzige ist.
 * Ein Ort ist eine Behauptung darüber, wo jemand abends hinfährt.
 *
 * Ohne Netz ist die Suche nicht da, und das steht dann auch da. Blockiert wird
 * nichts: die Zahlenfelder daneben bleiben bedienbar, und wer die Koordinate
 * aus einer anderen Quelle hat, trägt sie ein.
 */
export function AddressSearch({
  onPick,
  platzhalter,
  vorgabe = '',
  beschriftung = 'Adresse suchen',
}: {
  /** Wird gerufen, wenn der Nutzer einen Treffer ausgewählt hat. */
  onPick: (at: LatLon, place: Place) => void
  platzhalter: string
  /** Vorschlag für das Suchfeld, etwa der Name des Platzes. */
  vorgabe?: string
  /**
   * Beschriftung des Suchfelds.
   *
   * Sie ist einstellbar, seit in derselben Bearbeitung zwei Suchen stehen
   * können - eine für den Start der ersten Etappe, eine für ihr Ziel. Zweimal
   * „Adresse suchen“ untereinander sagt nicht, welche welche ist.
   */
  beschriftung?: string
}) {
  const suchen = useSearchPlaces()
  const online = useOnline()
  const [text, setText] = useState(vorgabe)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [treffer, setTreffer] = useState<Place[] | null>(null)
  const abbruch = useRef<AbortController | null>(null)

  // Wer die Etappe zuklappt, während die Anfrage läuft, hat es sich anders
  // überlegt. Ohne den Abbruch schriebe die Antwort danach noch in einen
  // Zustand, den niemand mehr ansieht.
  useEffect(
    () => () => {
      abbruch.current?.abort()
    },
    [],
  )

  const starten = async () => {
    const begriff = text.trim()
    if (begriff === '' || laeuft) return

    setFehler(null)
    setTreffer(null)
    setLaeuft(true)
    const controller = new AbortController()
    abbruch.current?.abort()
    abbruch.current = controller
    try {
      setTreffer(await suchen(begriff, controller.signal))
    } catch (error) {
      if (controller.signal.aborted) return
      setFehler(geocodeErrorText(error))
    } finally {
      if (abbruch.current === controller) abbruch.current = null
      setLaeuft(false)
    }
  }

  return (
    <div className="adresssuche kein-druck">
      <div className="adresssuche-zeile">
        <label className="feld">
          <span>{beschriftung}</span>
          <input
            value={text}
            autoComplete="off"
            placeholder={platzhalter}
            disabled={!online}
            onChange={(e) => setText(e.target.value)}
            // Enter im Formularfeld sucht, statt die Seite neu zu laden. Ein
            // eigenes form-Element gibt es hier nicht: dieses Bauteil steht
            // mitten in einem Bearbeitungsblock, und ein Formular im Formular
            // wäre ungültiges HTML.
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void starten()
              }
            }}
          />
        </label>
        <button
          type="button"
          className="knopf"
          disabled={!online || laeuft || text.trim() === ''}
          onClick={() => void starten()}
        >
          {laeuft ? <Loader2 size={14} className="dreht" /> : <Search size={14} />}
          {laeuft ? 'Sucht …' : 'Suchen'}
        </button>
      </div>

      {!online && (
        <p className="leer">
          Ohne Netz keine Adresssuche. Die Koordinate lässt sich unten von Hand eintragen.
        </p>
      )}

      {fehler && <p className="karte-warnung">{fehler}</p>}

      {treffer && treffer.length === 0 && (
        <p className="leer">
          Nichts gefunden. Anders schreiben, Ort oder Land dazu — oder die Koordinate von Hand
          eintragen.
        </p>
      )}

      {treffer && treffer.length > 0 && (
        <>
          <ul className="trefferliste">
            {treffer.map((ort, i) => (
              <li key={`${String(i)}-${ort.name}`}>
                <button
                  type="button"
                  className="treffer"
                  onClick={() => {
                    onPick(ort.at, ort)
                    // Die Liste verschwindet nach der Wahl: sie hat ihre Aufgabe
                    // erfüllt, und stehen bliebe sonst eine Auswahl, von der
                    // nicht mehr zu sehen ist, welche Zeile gilt.
                    setTreffer(null)
                  }}
                >
                  <MapPin size={14} />
                  <span>
                    <b>{ort.name}</b>
                    <small>
                      {coordText(ort.at)}
                      {ort.kind && ` · ${ort.kind}`}
                    </small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="knopfreihe">
            <button type="button" className="knopf still" onClick={() => setTreffer(null)}>
              <X size={14} /> Treffer verwerfen
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Ist gerade Netz da?
 *
 * navigator.onLine ist keine Zusage - im Funkloch hinter einem Hotspot steht
 * dort weiterhin true, und die Suche scheitert dann mit einer verständlichen
 * Meldung (siehe geocodeErrorText). Umgekehrt ist es aber verlässlich: sagt es
 * false, ist mit Sicherheit nichts zu holen, und dann soll das Feld gar nicht
 * erst so tun, als ginge es.
 */
function useOnline(): boolean {
  return useSyncExternalStore(
    (aendert) => {
      window.addEventListener('online', aendert)
      window.addEventListener('offline', aendert)
      return () => {
        window.removeEventListener('online', aendert)
        window.removeEventListener('offline', aendert)
      }
    },
    () => navigator.onLine,
    // Beim Rendern auf dem Server gäbe es keinen navigator. Die App wird nicht
    // serverseitig gerendert; der Wert steht hier, damit der Aufruf vollständig
    // ist und nicht, weil es einen Fall gäbe.
    () => true,
  )
}
